<template>
  <div>
    <h2 class="mc-h2">Let's find your cover</h2>
    <p class="mc-lede">
      Are you reporting this as the owner, or on the owner's behalf?
    </p>

    <segmented-control
      class="mc-mb20"
      :options="[
        { label: 'Owner', value: false },
        { label: 'Owner’s behalf', value: true },
      ]"
      :value="value.onBehalf"
      @input="update('onBehalf', $event)"
    />

    <div class="mc-row2">
      <div class="mc-field">
        <label class="mc-lbl" for="mc-reg"
          >Registration number <span class="mc-req">*</span></label
        >
        <input
          id="mc-reg"
          type="text"
          autocapitalize="characters"
          placeholder="KDA 123X"
          :value="value.reg"
          @input="update('reg', $event.target.value.toUpperCase())"
        />
        <p class="mc-hint">Letters and numbers, spaces optional</p>
      </div>

      <div class="mc-field">
        <label class="mc-lbl" for="mc-phone"
          >Contact number <span class="mc-req">*</span></label
        >
        <input
          id="mc-phone"
          type="tel"
          inputmode="tel"
          placeholder="07XX XXX XXX"
          :value="value.phone"
          @input="update('phone', $event.target.value)"
        />
        <p class="mc-hint">
          We'll text your claim reference — it doesn't need to match the policy.
        </p>
      </div>
    </div>

    <div
      v-if="lookupFailed"
      class="mc-card-error mc-mt24"
      data-testid="lookup-error"
    >
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.9"
        stroke-linecap="round"
        class="mc-error-icon"
      >
        <path
          d="M10.3 3.9 2.5 18a1.9 1.9 0 0 0 1.7 2.8h15.6a1.9 1.9 0 0 0 1.7-2.8L13.7 3.9a1.9 1.9 0 0 0-3.4 0Z"
        />
        <path d="M12 9v4.2" />
        <circle cx="12" cy="16.9" r=".9" fill="currentColor" stroke="none" />
      </svg>
      <div class="mc-info-copy">
        <b>There was an error checking your information.</b> Please complete
        your claim through our back office &mdash; call
        <a href="tel:+254709912777">0709 912 777</a> or email
        <a href="mailto:claims@apainsurance.org">claims@apainsurance.org</a>.
      </div>
    </div>

    <div class="mc-card-soft mc-mt24">
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="1.9"
        stroke-linecap="round"
        class="mc-info-icon"
      >
        <circle cx="12" cy="12" r="9" />
        <path d="M12 11v5" />
        <circle cx="12" cy="7.8" r=".9" fill="currentColor" stroke="none" />
      </svg>
      <div class="mc-info-copy">
        Reporting within <b>48 hours</b> of the incident keeps your claim on the
        fastest track.
      </div>
    </div>
  </div>
</template>

<script>
import SegmentedControl from '../shared/SegmentedControl.vue'

export default {
  name: 'LookupStep',
  components: { SegmentedControl },
  props: {
    // { onBehalf, reg, phone }
    value: { type: Object, required: true },
    // shows the back-office card when a policy lookup came back unsuccessful
    lookupFailed: { type: Boolean, default: false },
  },
  methods: {
    update(key, val) {
      this.$emit('input', { ...this.value, [key]: val })
    },
  },
}
</script>

<style scoped>
.mc-h2 {
  font-size: 19px;
  line-height: 1.3;
  font-weight: 700;
  margin: 0 0 6px;
  color: #14142b;
}

.mc-lede {
  font-size: 14.5px;
  color: #6e7191;
  margin: 0 0 20px;
}

.mc-mb20 {
  margin-bottom: 20px;
}

.mc-mt24 {
  margin-top: 24px;
}

.mc-field {
  margin: 0 0 16px;
}

/* short fields pair up two-across at 900px+ instead of stretching solo
   across the now-wider content column */
@media (min-width: 900px) {
  .mc-row2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  .mc-row2 .mc-field {
    margin-bottom: 0;
  }
}

.mc-lbl {
  display: block;
  font-size: 13px;
  font-weight: 600;
  color: #3d4450;
  margin-bottom: 7px;
}

.mc-req {
  color: #eb5757;
}

input[type='text'],
input[type='tel'] {
  width: 100%;
  font-family: inherit;
  font-size: 16px;
  color: #333333;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  padding: 13px 14px;
  appearance: none;
  transition: border-color 0.15s, box-shadow 0.15s;
}

input:focus {
  outline: 0;
  border-color: #0066cc;
  box-shadow: 0 0 0 3px rgba(0, 102, 204, 0.13);
}

.mc-hint {
  font-size: 12.5px;
  color: #6e7191;
  margin: 6px 0 0;
}

.mc-card-soft {
  display: flex;
  gap: 11px;
  background: #eef3ff;
  border: 1px solid #bdd8f5;
  border-radius: 14px;
  padding: 16px;
}

.mc-info-icon {
  color: #0052b3;
  flex: 0 0 auto;
  margin-top: 1px;
}

.mc-card-error {
  display: flex;
  gap: 11px;
  background: #fff5f5;
  border: 1px solid #f2c0c0;
  border-radius: 14px;
  padding: 16px;
}

.mc-error-icon {
  color: #c1121f;
  flex: 0 0 auto;
  margin-top: 1px;
}

.mc-card-error a {
  color: #c1121f;
  font-weight: 600;
  white-space: nowrap;
}

.mc-info-copy {
  font-size: 12.5px;
  color: #002247;
  line-height: 1.5;
}
</style>
